<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.groups', 2),
                        href: meta.links.group_index,
                    },
                    {
                        name: group.name,
                        href: meta.links.group_show,
                    },
                    {
                        name: $tc('messages.gamification_requests', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="flex space-x-2 items-center">
            <NavLinks :links="meta.navLinks" />
        </div>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div class="sm:col-span-4">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.status')" for="status" />
                        <FormSelect
                            required
                            id="gradable"
                            v-model="form.status"
                            :options="options.status"
                            class="mt-1 w-full"
                            :null-option="false"
                        />
                        <FormError :value="form.errors.status" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-8">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <EmptyState picture v-if="!gamificationRequests.data.length">
            <template v-slot:picture />
        </EmptyState>
        <div class="flex flex-col mt-5" v-else>
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-1 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <SelectAllNoneButtons
                                        :max-count="gamificationRequests.data.length"
                                        :selected-count="submitForm.gamification_requests_ids.length"
                                        @select-all="selectAll()"
                                        @select-none="submitForm.gamification_requests_ids = []"
                                        class="flex md:w-12"
                                        v-if="isShowingRequests"
                                    />
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.badges', 1) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.student') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.message') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.when') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(gamificationRequest, index) in gamificationRequests.data"
                                :key="gamificationRequest.id"
                            >
                                <td
                                    class="flex justify-center px-1 py-4 whitespace-nowrap text-sm leading-5 text-gray-500"
                                >
                                    <input
                                        type="checkbox"
                                        :value="gamificationRequest.id"
                                        v-model="submitForm.gamification_requests_ids"
                                        name="gamificationRequest"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                        v-if="isShowingRequests"
                                    />
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div class="flex space-x-2 items-center">
                                        <img :src="gamificationRequest.image_path" alt="" class="h-6" />
                                        <div>
                                            <div>
                                                <InertiaLink class="text-indigo-500" :href="gamificationRequest.url">
                                                    {{ gamificationRequest.requested_name }}
                                                </InertiaLink>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div>{{ gamificationRequest.student_name }}</div>
                                    <div class="mt-1">
                                        <BadgePill color="green" v-if="gamificationRequest.status === 'granted'">
                                            {{ $t('messages.request_granted') }}
                                        </BadgePill>
                                        <BadgePill color="yellow" v-else-if="gamificationRequest.status === 'rejected'">
                                            {{ $t('messages.request_denied') }}
                                        </BadgePill>
                                        <BadgePill color="blue" v-else-if="gamificationRequest.status === 'pending'">
                                            {{ $t('messages.request_pending') }}
                                        </BadgePill>
                                        <span v-if="form.type === 'reward'" v-show="meta.can.canModifyRewardPercent">
                                            <Tippy>
                                                <BadgePill color="gray"> {{ gamificationRequest.percent }}% </BadgePill>

                                                <template #content>
                                                    {{ $t('messages.percent_of_reward_requested') }}
                                                </template>
                                            </Tippy>
                                        </span>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-pre-line max-w-sm text-sm leading-5 text-gray-500">
                                    {{ gamificationRequest.message }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div :class="{ 'text-gray-300': gamificationRequest.is_updated }">
                                        {{
                                            $t('messages.requested_ago', {
                                                ago: gamificationRequest.created_at_diff_for_humans,
                                            })
                                        }}
                                    </div>
                                    <div v-if="gamificationRequest.is_updated">
                                        {{
                                            $t('messages.updated_ago', {
                                                ago: gamificationRequest.updated_at_diff_for_humans,
                                            })
                                        }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex justify-end space-x-1"
                                >
                                    <inertia-link
                                        v-if="isShowingRequests"
                                        preserve-scroll
                                        method="patch"
                                        as="button"
                                        :href="meta.links.update"
                                        :data="{
                                            gamification_requests_ids: [gamificationRequest.id],
                                            status: 'confirm',
                                        }"
                                        class="px-3 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 inline-flex items-center border border-transparent font-medium rounded shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                        {{ $t('messages.confirm') }}
                                    </inertia-link>
                                    <inertia-link
                                        v-if="isShowingRequests"
                                        preserve-scroll
                                        method="patch"
                                        as="button"
                                        :href="meta.links.update"
                                        :data="{
                                            gamification_requests_ids: [gamificationRequest.id],
                                            status: 'reject',
                                        }"
                                        class="inline-flex items-center border border-gray-300 shadow-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 text-gray-700 bg-white hover:bg-gray-50 px-3 py-2 text-sm"
                                    >
                                        {{ $t('messages.reject') }}
                                    </inertia-link>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="flex space-x-2 items-center mt-3" v-if="submitForm.gamification_requests_ids.length">
            <form action="" @submit.prevent="submitConfirm()">
                <AppButton desired-action element="button" as="button" :disabled="submitForm.processing">
                    {{ $t('messages.confirm') }}
                    <span v-if="submitForm.processing" class="ml-1 btn-spinner"></span>
                </AppButton>
            </form>

            <form action="" @submit.prevent="submitReturn()">
                <AppButton element="button" as="button" :disabled="submitForm.processing">
                    {{ $t('messages.return_item') }}
                    <span v-if="submitForm.processing" class="ml-1 btn-spinner"></span>
                </AppButton>
            </form>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="gamificationRequests" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppButton from '@/components/AppButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import SelectAllNoneButtons from '@/components/SelectAllNoneButtons.vue';
import NavLinks from '@/components/NavLinks.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormError from '@/components/Forms/FormError.vue';
import BadgePill from '@/components/BadgePill.vue';
import { Tippy } from 'vue-tippy';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        Tippy,
        BadgePill,
        FormError,
        FormSelect,
        FormLabel,
        PanelPage,
        AppButton,
        EmptyState,
        InertiaPagination,
        NavLinks,
        SelectAllNoneButtons,
    },
    layout: Layout,
    name: 'Show',
    props: ['filters', 'meta', 'options', 'group', 'gamificationRequests'],
    data() {
        return {
            form: this.$inertia.form({
                status: this.filters.status,
                type: this.filters.type,
            }),
            submitForm: this.$inertia.form({
                gamification_requests_ids: [],
                status: 'confirm',
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
        submit() {
            this.submitForm.patch(this.meta.links.update, {
                preserveScroll: true,
                onSuccess: () => {
                    this.submitForm.reset();
                },
            });
        },
        submitConfirm() {
            this.submitForm.status = 'confirm';
            this.submit();
        },
        submitReturn() {
            this.submitForm.status = 'reject';
            this.submit();
        },
        selectAll() {
            this.submitForm.gamification_requests_ids = this.gamificationRequests.data.map((i) => i.id);
        },
    },
    computed: {
        isShowingRequests: function () {
            return this.filters.status === 'requested';
        },
    },
};
</script>
